import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Select, Card, Badge } from "../components/ui";

const DAYS_OF_WEEK = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTHS = [
  "Gennaio",
  "Febbraio",
  "Marzo",
  "Aprile",
  "Maggio",
  "Giugno",
  "Luglio",
  "Agosto",
  "Settembre",
  "Ottobre",
  "Novembre",
  "Dicembre",
];

const STATO_SCADUTO = 100000002;

// Event color based on scadenza state
const getEventColor = (event) =>
  event.stato === STATO_SCADUTO ? "bg-red-500" : "bg-amber-500";

function CalendarioPage() {
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();

  // User role checks
  const isAdmin = user?.ruolo === 100000000;
  const isRSPP = user?.ruolo === 100000001;
  const isDirezione = user?.ruolo === 100000002;
  const isFornitore = user?.ruolo === 100000005;
  const canViewAll = isAdmin || isRSPP || isDirezione;

  // State
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rsaList, setRsaList] = useState([]);
  const [rsaFilter, setRsaFilter] = useState("");
  const [fornitoreFilter, setFornitoreFilter] = useState("");
  const [tipologiaFilter, setTipologiaFilter] = useState("");
  const [selectedDay, setSelectedDay] = useState(null);
  const [viewMode, setViewMode] = useState("month"); // 'month' or 'week'

  // Calculate month boundaries
  const monthStart = useMemo(() => {
    const d = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    return d;
  }, [currentDate]);

  const monthEnd = useMemo(() => {
    const d = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0,
    );
    return d;
  }, [currentDate]);

  // Calculate calendar grid (6 weeks max)
  const calendarDays = useMemo(() => {
    const days = [];
    const startDay = new Date(monthStart);

    // Adjust to start from Monday
    const dayOfWeek = startDay.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startDay.setDate(startDay.getDate() - daysToSubtract);

    // Generate 42 days (6 weeks)
    for (let i = 0; i < 42; i++) {
      const d = new Date(startDay);
      d.setDate(startDay.getDate() + i);
      days.push(d);
    }

    return days;
  }, [monthStart]);

  // Fetch RSA list for filter
  useEffect(() => {
    if (!canViewAll) return;

    const fetchRsa = async () => {
      try {
        const result = await apiRequest("/rsa");
        setRsaList(result.value || []);
      } catch (error) {
        console.error("Error fetching RSA:", error);
      }
    };
    fetchRsa();
  }, [apiRequest, canViewAll]);

  // Fetch events
  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        // Calculate date range (current month + 1 week before/after)
        const startDate = new Date(monthStart);
        startDate.setDate(startDate.getDate() - 7);
        const endDate = new Date(monthEnd);
        endDate.setDate(endDate.getDate() + 7);

        const params = new URLSearchParams({
          start: startDate.toISOString(),
          end: endDate.toISOString(),
        });

        // La visibilita' per ruolo e' applicata dal backend
        if (canViewAll && rsaFilter) {
          params.append("rsaId", rsaFilter);
        }

        const result = await apiRequest(`/calendario/scadenze?${params.toString()}`);
        setEvents(result.value || []);
      } catch (error) {
        console.error("Error fetching calendar events:", error);
        showToast("Errore nel caricamento degli eventi", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [
    apiRequest,
    monthStart,
    monthEnd,
    rsaFilter,
    canViewAll,
  ]);

  // Opzioni dei filtri: fornitori e tipologie presenti nel periodo caricato
  const fornitoreOptions = useMemo(() => {
    const map = new Map();
    events.forEach((e) => {
      if (e.fornitoreId) map.set(e.fornitoreId, e.fornitoreNome);
    });
    return [...map].sort((a, b) => (a[1] || "").localeCompare(b[1] || ""));
  }, [events]);

  const tipologiaOptions = useMemo(() => {
    const map = new Map();
    events.forEach((e) =>
      (e.tipologie || []).forEach((t) => map.set(t.id, t.nome)),
    );
    return [...map].sort((a, b) => (a[1] || "").localeCompare(b[1] || ""));
  }, [events]);

  const filteredEvents = useMemo(
    () =>
      events.filter(
        (e) =>
          (!fornitoreFilter || e.fornitoreId === fornitoreFilter) &&
          (!tipologiaFilter ||
            (e.tipologie || []).some((t) => t.id === tipologiaFilter)),
      ),
    [events, fornitoreFilter, tipologiaFilter],
  );

  // Get events for a specific day
  const getEventsForDay = (day) => {
    return filteredEvents.filter((event) => {
      const eventDate = new Date(event.start?.dateTime || event.start);
      return (
        eventDate.getFullYear() === day.getFullYear() &&
        eventDate.getMonth() === day.getMonth() &&
        eventDate.getDate() === day.getDate()
      );
    });
  };

  // Navigation handlers
  const goToPreviousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
    );
    setSelectedDay(null);
  };

  const goToNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    );
    setSelectedDay(null);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDay(null);
  };

  // Format time
  const formatEventTime = (event) => {
    if (event.isAllDay) return "Tutto il giorno";
    const date = new Date(event.start?.dateTime || event.start);
    return date.toLocaleTimeString("it-IT", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Selected day events
  const selectedDayEvents = selectedDay ? getEventsForDay(selectedDay) : [];

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Calendario Scadenze
          </h1>
          <p className="text-gray-600">
            {canViewAll
              ? "Vista aggregata di tutti i calendari RSA"
              : "Le tue scadenze programmate"}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/scadenze">
            <Button variant="secondary">Vista Lista</Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex gap-4 flex-wrap items-center">
          {canViewAll && (
            <label className="text-sm font-medium text-gray-700">
              RSA:
              <select
                value={rsaFilter}
                onChange={(e) => setRsaFilter(e.target.value)}
                className="input w-56 ml-2"
              >
                <option value="">Tutte le RSA</option>
                {rsaList.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nome}
                  </option>
                ))}
              </select>
            </label>
          )}
          {!isFornitore && (
            <label className="text-sm font-medium text-gray-700">
              Fornitore:
              <select
                value={fornitoreFilter}
                onChange={(e) => setFornitoreFilter(e.target.value)}
                className="input w-56 ml-2"
              >
                <option value="">Tutti i fornitori</option>
                {fornitoreOptions.map(([id, nome]) => (
                  <option key={id} value={id}>
                    {nome}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="text-sm font-medium text-gray-700">
            Tipologia:
            <select
              value={tipologiaFilter}
              onChange={(e) => setTipologiaFilter(e.target.value)}
              className="input w-56 ml-2"
            >
              <option value="">Tutte le tipologie</option>
              {tipologiaOptions.map(([id, nome]) => (
                <option key={id} value={id}>
                  {nome}
                </option>
              ))}
            </select>
          </label>
          {(rsaFilter || fornitoreFilter || tipologiaFilter) && (
            <button
              onClick={() => {
                setRsaFilter("");
                setFornitoreFilter("");
                setTipologiaFilter("");
              }}
              className="text-sm text-primary-600 hover:underline"
            >
              Rimuovi filtri
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar Grid */}
        <div className="lg:col-span-3">
          <div className="card">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={goToPreviousMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Mese precedente"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <h2 className="text-xl font-semibold text-gray-900">
                  {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h2>
                <button
                  onClick={goToNextMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Mese successivo"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
              <button
                onClick={goToToday}
                className="px-3 py-1 text-sm bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 transition-colors"
              >
                Oggi
              </button>
            </div>

            {/* Days of week header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAYS_OF_WEEK.map((day) => (
                <div
                  key={day}
                  className="text-center text-sm font-medium text-gray-500 py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            {loading ? (
              <div className="flex items-center justify-center h-96">
                <div className="spinner w-10 h-10"></div>
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, index) => {
                  const isCurrentMonth =
                    day.getMonth() === currentDate.getMonth();
                  const isToday =
                    day.toDateString() === new Date().toDateString();
                  const isSelected =
                    selectedDay &&
                    day.toDateString() === selectedDay.toDateString();
                  const dayEvents = getEventsForDay(day);
                  const hasEvents = dayEvents.length > 0;

                  return (
                    <button
                      key={index}
                      onClick={() => setSelectedDay(day)}
                      className={`
                        min-h-24 p-2 border rounded-lg text-left transition-all
                        ${isCurrentMonth ? "bg-white" : "bg-gray-50"}
                        ${isToday ? "ring-2 ring-primary-500" : ""}
                        ${isSelected ? "border-primary-500 bg-primary-50" : "border-gray-200"}
                        ${hasEvents ? "hover:shadow-md" : "hover:bg-gray-50"}
                      `}
                    >
                      <div
                        className={`
                        text-sm font-medium mb-1
                        ${isCurrentMonth ? "text-gray-900" : "text-gray-400"}
                        ${isToday ? "bg-primary-500 text-white w-6 h-6 rounded-full flex items-center justify-center" : ""}
                      `}
                      >
                        {day.getDate()}
                      </div>

                      {/* Events preview */}
                      <div className="space-y-1">
                        {dayEvents.slice(0, 3).map((event, i) => (
                          <div
                            key={event.id || i}
                            className={`
                              text-xs px-1 py-0.5 rounded truncate text-white
                              ${getEventColor(event)}
                            `}
                            title={event.subject}
                          >
                            {event.subject?.substring(0, 15)}
                            {event.subject?.length > 15 ? "..." : ""}
                          </div>
                        ))}
                        {dayEvents.length > 3 && (
                          <div className="text-xs text-gray-500 px-1">
                            +{dayEvents.length - 3} altri
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Legend */}
            <div className="mt-4 pt-4 border-t flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded bg-amber-500"></span>
                <span className="text-gray-600">Scadenza</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded bg-red-500"></span>
                <span className="text-gray-600">Scaduta</span>
              </div>
            </div>
          </div>
        </div>

        {/* Day Detail Sidebar */}
        <div className="lg:col-span-1">
          <div className="card sticky top-4">
            <h3 className="font-semibold text-gray-900 mb-4">
              {selectedDay ? (
                <>
                  {selectedDay.toLocaleDateString("it-IT", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  })}
                </>
              ) : (
                "Seleziona un giorno"
              )}
            </h3>

            {selectedDay && (
              <>
                {selectedDayEvents.length > 0 ? (
                  <div className="space-y-3">
                    {selectedDayEvents.map((event, i) => (
                      <div
                        key={event.id || i}
                        className="p-3 border rounded-lg hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-start gap-2">
                          <span
                            className={`w-2 h-2 rounded-full mt-1.5 ${getEventColor(event)}`}
                          ></span>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {event.subject}
                            </p>
                            <p className="text-sm text-gray-500">
                              {formatEventTime(event)}
                            </p>
                            {event.rsaNome && (
                              <p className="text-xs text-gray-400 mt-1">
                                RSA: {event.rsaNome}
                              </p>
                            )}
                            {event.fornitoreNome && (
                              <p className="text-xs text-gray-400">
                                Fornitore: {event.fornitoreNome}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Event actions */}
                        <div className="mt-2 pt-2 border-t flex gap-2">
                          <Link
                            to={`/scadenze/${event.id}`}
                            className="text-xs text-primary-600 hover:underline"
                          >
                            Apri scadenza
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">
                    Nessun evento programmato per questo giorno.
                  </p>
                )}

                {/* Quick actions */}
                {(isAdmin || isRSPP || isFornitore) && (
                  <div className="mt-4 pt-4 border-t">
                    <Link
                      to={`/scadenze/nuova?data=${selectedDay.toISOString().split("T")[0]}`}
                      className="text-sm text-primary-600 hover:underline"
                    >
                      + Crea nuova scadenza
                    </Link>
                  </div>
                )}
              </>
            )}

            {!selectedDay && (
              <p className="text-gray-500 text-sm">
                Clicca su un giorno per vedere gli eventi programmati.
              </p>
            )}
          </div>

          {/* Statistics Card */}
          <div className="card mt-4">
            <h3 className="font-semibold text-gray-900 mb-3">Riepilogo Mese</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Eventi totali:</span>
                <span className="font-medium">{filteredEvents.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Giorni con eventi:</span>
                <span className="font-medium">
                  {
                    new Set(
                      filteredEvents.map((e) =>
                        new Date(e.start?.dateTime || e.start).toDateString(),
                      ),
                    ).size
                  }
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CalendarioPage;
